import { Box, Divider, Typography, useMediaQuery } from '@mui/material'
import { useQueryClient } from 'react-query'
import CircleIcon from '@mui/icons-material/Circle'
import Modal from 'components/Modal/Modal'
import { useState } from 'react'
import { t } from 'i18next'
import { EDIT_DELIVERY_AREA_TYPE } from 'types/api/serviceAreas'
import theme, { routePlanningColors } from 'lib/theme'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { useAlert } from 'providers/AlertProvider'
import { useDeleteArea, usePostNewAreaCoordinates } from 'hooks/useServiceAreas'
import { DeliveryAreaGroup } from 'types/model/common'
import { SidebarBtn } from './ServiceArea.styled'
import { getColorFromIndex } from './utils'
import { SearchDeliveryGroups } from './SearchDeliveryGroups'

type Props = {
  modal: { right: boolean }
  handleCloseModal: () => void
  placeId: string
  type: EDIT_DELIVERY_AREA_TYPE
  currentDeliveryAreaId: number
}

type SubmitValues = {
  delivery_areas: {
    area: number[][]
    label: string | null
  }[]
}

export const CopyAreaModal = ({
  modal,
  handleCloseModal,
  placeId,
  type,
  currentDeliveryAreaId,
}: Props) => {
  const {
    allDeliveryAreas,
    selectedDeliveryArea: selectedDeliveryAreaGroup,
    coordinates,
  } = useServiceAreas()
  const [selectedAreaGroup, setSelectedAreaGroup] =
    useState<DeliveryAreaGroup | null>(null)
  // track which delivery areas need to be refetched after closing modal
  const [ids, setIds] = useState<number[]>([])
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>(placeId)
  const queryClient = useQueryClient()
  const {
    mutateAsync: mutatePostCoordinates,
    isLoading: isLoadingPostRequest,
  } = usePostNewAreaCoordinates(
    selectedPlaceId as string,
    selectedAreaGroup?.id ?? -1,
    true
  )
  const { mutateAsync: mutateDeleteAsync, isLoading: isLoadingDeleteRequest } =
    useDeleteArea(
      placeId as string,
      selectedDeliveryAreaGroup.id,
      currentDeliveryAreaId as number,
      selectedAreaGroup ? [selectedAreaGroup?.id] : []
    )
  const deliveryAreaGroupIndex = allDeliveryAreas.findIndex(
    (area) => area.id === selectedDeliveryAreaGroup.id
  )
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // remove current delivery area group from list of delivery groups
  const deliveryAreas = [...allDeliveryAreas]
  deliveryAreas.splice(deliveryAreaGroupIndex, 1)
  const availableColors = [...routePlanningColors]
  availableColors.splice(deliveryAreaGroupIndex, 1)

  const buttonText =
    type === EDIT_DELIVERY_AREA_TYPE.COPY
      ? t('copyArea', { selectedDeliveryArea: '' })
      : t('moveArea', { selectedDeliveryArea: '' })
  const title =
    type === EDIT_DELIVERY_AREA_TYPE.COPY ? t('copyAreaTo') : t('moveAreaTo')

  const handleClose = () => {
    if (ids.length > 0) {
      ids.forEach((id) => {
        queryClient.fetchQuery(['getCoordinates', id + placeId])
      })
      queryClient.fetchQuery(['getDeliveryAreaGroups', placeId])
    }
    handleCloseModal()
  }

  const handleCreateArea = async (values: SubmitValues) => {
    if (!selectedAreaGroup) return
    await mutatePostCoordinates(values, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        setIds([...ids, selectedAreaGroup.id])
        setSelectedAreaGroup(null)
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const handleDeleteArea = async (values: SubmitValues) => {
    if (!selectedAreaGroup) return
    await mutatePostCoordinates(values, {
      onSuccess: async () => {
        await mutateDeleteAsync(
          {},
          {
            onSuccess: () => {
              setAlert(t('saved'), 'success', {
                vertical: 'bottom',
                horizontal: isMobile ? 'center' : 'left',
              })
              handleClose()
            },
            onError: () => {
              setAlert(t('failureMessage'), 'failure', {
                vertical: 'bottom',
                horizontal: isMobile ? 'center' : 'left',
              })
            },
          }
        )
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const handleSubmit = async () => {
    const selectedCoordinates = coordinates?.[0]?.[0]
    if (!selectedAreaGroup) return

    const values = {
      delivery_areas: [
        {
          area: selectedCoordinates?.area,
          label: selectedCoordinates?.label ?? null,
        },
      ],
    }
    if (type === EDIT_DELIVERY_AREA_TYPE.COPY) {
      handleCreateArea(values)
    } else {
      handleDeleteArea(values)
    }
  }

  return (
    <Modal
      showButton
      onButtonClick={handleSubmit}
      buttonIsDisabled={!selectedAreaGroup}
      buttonText={buttonText}
      isLoadingRequest={isLoadingPostRequest || isLoadingDeleteRequest}
      setModal={handleClose}
      modal={modal}
      title={title}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '300px',
          height: '50%',
        }}>
        {deliveryAreas.map((deliveryArea, index) => {
          return (
            <SidebarBtn
              selected={selectedAreaGroup?.id === deliveryArea.id}
              key={deliveryArea.id}
              onClick={() => setSelectedAreaGroup(deliveryArea)}>
              <Box display='flex' alignItems='center'>
                <CircleIcon
                  fontSize='small'
                  sx={{
                    fill: getColorFromIndex(index, availableColors),
                    '&:hover': {
                      fill: getColorFromIndex(index, availableColors),
                    },
                  }}
                />
                <Box>
                  <Typography
                    variant='body2'
                    ml={1}
                    sx={{ textAlign: 'start' }}>
                    {deliveryArea.name}
                  </Typography>
                  <Typography
                    variant='body2'
                    ml={1}
                    sx={{ textAlign: 'start' }}>
                    {deliveryArea.id}
                  </Typography>
                </Box>
              </Box>
            </SidebarBtn>
          )
        })}
        <Divider sx={{ marginTop: theme.spacing(4) }} />
        <SearchDeliveryGroups
          setSelectedAreaGroup={setSelectedAreaGroup}
          selectedAreaGroup={selectedAreaGroup}
          setSelectedPlaceId={setSelectedPlaceId}
        />
      </Box>
    </Modal>
  )
}
